// src/pages/RouteProtectionTestPage.jsx
import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { PERMISSIONS, ROUTE_PERMISSIONS } from '../utils/auth';
import { Shield, CheckCircle, XCircle, User, Settings } from 'lucide-react';
import { AccessLevelIndicator, RouteAccessIndicator, AccessSummary } from '../components/ui/AccessLevelIndicator';

/**
 * Test page to verify route protection is working correctly
 * This page shows current user permissions and route access
 */
export default function RouteProtectionTestPage() {
  const {
    user,
    userDisplayName,
    userRoleDisplayName,
    hasPermission,
    hasRole,
    canAccessRoute,
    isAdmin,
    isSuperAdmin,
    isUser
  } = useAuth();

  // Test routes to check access
  const testRoutes = [
    { path: '/dashboard', name: 'Dashboard' },
    { path: '/users', name: 'Users' },
    { path: '/users/add', name: 'Add User' },
    { path: '/cars', name: 'Cars' },
    { path: '/cars/add', name: 'Add Car' },
    { path: '/locations', name: 'Locations' },
    { path: '/trips', name: 'Trips' },
    { path: '/finance', name: 'Finance' },
    { path: '/notifications', name: 'Notifications' }
  ];

  // Test permissions
  const testPermissions = [
    'dashboard_view',
    'users_view',
    'users_create',
    'users_edit',
    'users_delete',
    'users_manage',
    'cars_view',
    'cars_create',
    'cars_edit',
    'cars_manage',
    'locations_view',
    'locations_manage',
    'trips_view',
    'trips_manage',
    'notifications_view',
    'system_admin'
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-3 mb-4">
          <Shield className="w-8 h-8 text-indigo-600" />
          <h1 className="text-3xl font-bold text-gray-900">
            Route Protection Test
          </h1>
        </div>
        <p className="text-gray-600">
          This page shows your current permissions and route access levels
        </p>
      </div>

      {/* Current User Info */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <User className="w-5 h-5" />
          Current User Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Name</label>
            <p className="text-lg text-gray-900">{userDisplayName}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500">Role</label>
            <div className="flex items-center gap-2">
              <p className="text-lg text-gray-900">{userRoleDisplayName}</p>
              <AccessLevelIndicator size="sm" />
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500">Email</label>
            <p className="text-lg text-gray-900">{user?.email}</p>
          </div>
          
          <div>
            <label className="text-sm font-medium text-gray-500">Access Levels</label>
            <div className="mt-1">
              <AccessSummary />
            </div>
          </div>
        </div>
      </div>

      {/* Route Access Test */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Route Access Test
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testRoutes.map(route => {
            const hasAccess = canAccessRoute(route.path);
            return (
              <div
                key={route.path}
                className={`p-4 rounded-lg border-2 ${
                  hasAccess 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-red-200 bg-red-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{route.name}</h3>
                    <p className="text-sm text-gray-500">{route.path}</p>
                  </div>
                  <RouteAccessIndicator route={route.path} size="md" />
                </div>
                <p className={`text-xs mt-2 ${hasAccess ? 'text-green-700' : 'text-red-700'}`}>
                  {hasAccess ? 'Access Granted' : 'Access Denied'}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Permission Test */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Permission Test
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {testPermissions.map(permission => {
            const hasPermissionCheck = hasPermission(permission);
            return (
              <div
                key={permission}
                className={`p-3 rounded-lg border ${
                  hasPermissionCheck 
                    ? 'border-green-200 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">
                    {permission}
                  </span>
                  {hasPermissionCheck ? (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  ) : (
                    <XCircle className="w-4 h-4 text-gray-400" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Test Credentials */}
      <div className="bg-blue-50 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-blue-900 mb-3">
          Test Credentials
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Regular User</h3>
            <p className="text-gray-600">Email: user@example.com</p>
            <p className="text-gray-600">Password: user123</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Admin</h3>
            <p className="text-gray-600">Email: admin@example.com</p>
            <p className="text-gray-600">Password: admin123</p>
          </div>
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-medium text-gray-900 mb-2">Super Admin</h3>
            <p className="text-gray-600">Email: superadmin@example.com</p>
            <p className="text-gray-600">Password: super123</p>
          </div>
        </div>
      </div>
    </div>
  );
}
